import { ethers } from 'ethers'
import {
  AERO_ROUTER,
  CL3_QUOTER,
  CL_QUOTER,
  MULTICALL3,
  UNI_V2_ROUTER,
  UNI_V3_QUOTER
} from './addresses'
import {
  AERO_ROUTER_ABI,
  CL_QUOTER_ABI,
  MULTICALL3_ABI,
  UNI_V2_ROUTER_ABI,
  UNI_V3_QUOTER_ABI
} from './abi'
import { aeroRoutes, candidateV3Path, MAX_TWO_HOP_CANDIDATES, v2Path } from './path'
import { pairKey } from './discovery'
import { priceImpactBps } from './rank'
import type { OnChainVenue, PoolRef, Quote, RouteCandidate } from './types'

/**
 * What each route actually returns, asked in two multicall rounds.
 *
 * Round one prices every direct route and every first hop at full size. Round
 * two keeps only the best first hop per (hub, venue) — pruning is the whole
 * point, because without it the second round is the product of two pool sets
 * rather than the sum — and prices the second leg from there. Two rounds is
 * the budget: a page that costs four RPC round trips per keystroke is a page
 * that rate-limits the proxy it shares with every other screen.
 */

const aeroRouterIface = new ethers.utils.Interface(AERO_ROUTER_ABI as unknown as string[])
const uniV2RouterIface = new ethers.utils.Interface(UNI_V2_ROUTER_ABI as unknown as string[])
const uniV3QuoterIface = new ethers.utils.Interface(UNI_V3_QUOTER_ABI as unknown as string[])
const clQuoterIface = new ethers.utils.Interface(CL_QUOTER_ABI as unknown as string[])
const multicallIface = new ethers.utils.Interface(MULTICALL3_ABI as unknown as string[])

/** A thousandth of the trade is the reference size for price impact. */
export const REFERENCE_DIVISOR = 1000n

export interface QuoteCall {
  target: string
  allowFailure: true
  callData: string
  decode(returnData: string): bigint
}

function decodeLastAmount(iface: ethers.utils.Interface, fn: string) {
  return (returnData: string): bigint => {
    const decoded = iface.decodeFunctionResult(fn, returnData)
    const amounts = decoded[0] as ethers.BigNumber[]
    return amounts[amounts.length - 1].toBigInt()
  }
}

function decodeFirstAmount(iface: ethers.utils.Interface, fn: string) {
  return (returnData: string): bigint => {
    const decoded = iface.decodeFunctionResult(fn, returnData)
    return (decoded[0] as ethers.BigNumber).toBigInt()
  }
}

/**
 * One quote call for one candidate.
 *
 * The v3-family quoters are not `view` — they execute the swap and revert to
 * return the number — so they are read through `callStatic`, which is what
 * riding Multicall3 does anyway. A quoter that reverts because the pool cannot
 * fill the amount arrives as `success: false`, which means "no route", not an
 * error worth telling anybody about.
 */
export function quoteCallFor(candidate: RouteCandidate, amountIn: bigint): QuoteCall {
  const amount = ethers.BigNumber.from(amountIn.toString())
  switch (candidate.venue) {
    case 'aero-v2':
      return {
        target: AERO_ROUTER,
        allowFailure: true,
        callData: aeroRouterIface.encodeFunctionData('getAmountsOut', [amount, aeroRoutes(candidate)]),
        decode: decodeLastAmount(aeroRouterIface, 'getAmountsOut')
      }
    case 'uni-v2':
      return {
        target: UNI_V2_ROUTER,
        allowFailure: true,
        callData: uniV2RouterIface.encodeFunctionData('getAmountsOut', [amount, v2Path(candidate)]),
        decode: decodeLastAmount(uniV2RouterIface, 'getAmountsOut')
      }
    case 'uni-v3': {
      if (candidate.pools.length === 1) {
        const params = {
          tokenIn: candidate.path[0],
          tokenOut: candidate.path[1],
          amountIn: amount,
          fee: candidate.pools[0].fee,
          sqrtPriceLimitX96: 0
        }
        return {
          target: UNI_V3_QUOTER,
          allowFailure: true,
          callData: uniV3QuoterIface.encodeFunctionData('quoteExactInputSingle', [params]),
          decode: decodeFirstAmount(uniV3QuoterIface, 'quoteExactInputSingle')
        }
      }
      return {
        target: UNI_V3_QUOTER,
        allowFailure: true,
        callData: uniV3QuoterIface.encodeFunctionData('quoteExactInput', [candidateV3Path(candidate), amount]),
        decode: decodeFirstAmount(uniV3QuoterIface, 'quoteExactInput')
      }
    }
    /*
     * One Slipstream, two deployments. Factory 3 is the same periphery ABI at
     * a different address, so the only thing that differs between these two
     * cases is which quoter is asked — and asking the wrong one answers "no
     * pool" for a pool holding 1,7 M $ (scope 40 §1.5).
     */
    case 'aero-cl':
    case 'aero-cl3': {
      const quoter = candidate.venue === 'aero-cl3' ? CL3_QUOTER : CL_QUOTER
      if (candidate.pools.length === 1) {
        const params = {
          tokenIn: candidate.path[0],
          tokenOut: candidate.path[1],
          amountIn: amount,
          tickSpacing: candidate.pools[0].tickSpacing,
          sqrtPriceLimitX96: 0
        }
        return {
          target: quoter,
          allowFailure: true,
          callData: clQuoterIface.encodeFunctionData('quoteExactInputSingle', [params]),
          decode: decodeFirstAmount(clQuoterIface, 'quoteExactInputSingle')
        }
      }
      return {
        target: quoter,
        allowFailure: true,
        callData: clQuoterIface.encodeFunctionData('quoteExactInput', [candidateV3Path(candidate), amount]),
        decode: decodeFirstAmount(clQuoterIface, 'quoteExactInput')
      }
    }
    default:
      throw new Error(`no quoter for venue ${String(candidate.venue)}`)
  }
}

/** Direct routes: one pool, both tokens. */
export function directCandidates(
  byPair: Map<string, PoolRef[]>,
  tokenIn: string,
  tokenOut: string
): RouteCandidate[] {
  const pools = byPair.get(pairKey(tokenIn, tokenOut)) ?? []
  return pools.map((pool) => ({
    venue: pool.venue,
    path: [tokenIn.toLowerCase(), tokenOut.toLowerCase()],
    pools: [pool]
  }))
}

export interface HopGroup {
  hub: string
  venue: OnChainVenue
  first: PoolRef[]
  second: PoolRef[]
}

/**
 * The (hub, venue) groups a two-hop route could use. Both legs must exist on
 * the same venue: phase A never mixes venues in one transaction, because
 * doing so needs the Universal Router (§M).
 */
export function hopGroups(
  byPair: Map<string, PoolRef[]>,
  tokenIn: string,
  tokenOut: string,
  hubs: string[]
): HopGroup[] {
  const inAddr = tokenIn.toLowerCase()
  const outAddr = tokenOut.toLowerCase()
  const groups: HopGroup[] = []
  for (const rawHub of hubs) {
    const hub = rawHub.toLowerCase()
    if (hub === inAddr || hub === outAddr) continue
    const legOne = byPair.get(pairKey(inAddr, hub)) ?? []
    const legTwo = byPair.get(pairKey(hub, outAddr)) ?? []
    if (legOne.length === 0 || legTwo.length === 0) continue
    const venues = new Set<OnChainVenue>([...legOne, ...legTwo].map((p) => p.venue))
    for (const venue of venues) {
      const first = legOne.filter((p) => p.venue === venue)
      const second = legTwo.filter((p) => p.venue === venue)
      if (first.length === 0 || second.length === 0) continue
      groups.push({ hub, venue, first, second })
    }
  }
  /*
   * Sorted, then capped: which candidates survive has to be the same on every
   * run, or a route that quoted a moment ago disappears for no reason the user
   * can see. Venue first, hub order second — the order the caller passed.
   */
  const venueOrder: OnChainVenue[] = ['aero-v2', 'uni-v3', 'aero-cl', 'aero-cl3', 'uni-v2']
  const hubOrder = hubs.map((h) => h.toLowerCase())
  groups.sort((a, b) => {
    const v = venueOrder.indexOf(a.venue) - venueOrder.indexOf(b.venue)
    if (v !== 0) return v
    return hubOrder.indexOf(a.hub) - hubOrder.indexOf(b.hub)
  })

  const capped: HopGroup[] = []
  let budget = MAX_TWO_HOP_CANDIDATES
  for (const group of groups) {
    if (budget <= 0) break
    const pairs = group.second.length
    capped.push(group)
    budget -= pairs
  }
  return capped
}

function singleHopCandidate(from: string, to: string, pool: PoolRef): RouteCandidate {
  return { venue: pool.venue, path: [from, to], pools: [pool] }
}

interface Row {
  call: QuoteCall
  /** Where the decoded amount belongs. */
  slot: (amount: bigint) => void
}

/**
 * How many quoter calls ride in one `aggregate3`.
 *
 * A quoter does not read a price, it **executes the swap and reverts** to
 * return the number — so a round of them is a round of real swaps, and the
 * batch is charged against the node's `eth_call` gas cap. That cap is not the
 * same everywhere, and the platform rotates across a pool of providers.
 *
 * Measured 2026-09-11, one round for WETH→USDC (54 candidates, unchunked):
 * `mainnet.base.org` answers; `base-rpc.publicnode.com` and `base.drpc.org`
 * both revert with no data — two of the three providers in the pool. The page
 * then reports "price unavailable" for a pair with dozens of live pools, and
 * which provider answered decides whether it quotes at all.
 *
 * It is the gas, not the row count: forty copies of one cheap quoter call pass
 * on all three, while forty real rows — each walking a different pool's ticks —
 * do not, and the deeper the pools the fewer fit. Bisected on the narrowest
 * provider: WETH→USDC needs 20 or less, USDC→AAPLc needs 10 or less, and the
 * stock pools are the deep ones.
 *
 * Ten is therefore the default, and `callChunk` halves anything that still does
 * not fit — because a number that happens to work today is not a fix, it is the
 * same defect with a later date on it.
 */
const QUOTE_CHUNK = 10

/**
 * One `aggregate3`, halved and retried if the node refuses the whole batch.
 *
 * Every row already carries `allowFailure`, so a quoter that reverts because
 * its pool is empty costs that row and nothing else. What this handles is the
 * other revert — the one the *node* produces when the batch as a whole exceeds
 * its `eth_call` gas cap, which takes every row with it and leaves the page
 * saying it cannot price a pair with dozens of live pools.
 *
 * Only a `CALL_EXCEPTION` is split. A rate limit or a dead connection arrives
 * as a network error and is rethrown, because "we could not read the chain" and
 * "there is nothing here" are different sentences and scope 39 was spent
 * learning to tell them apart. At a single row a refusal is recorded as an
 * empty answer, which is what it is: this one route could not be priced.
 */
async function callChunk(
  multicall: ethers.Contract,
  calls: Array<{ target: string; allowFailure: boolean; callData: string }>
): Promise<Array<{ success: boolean; returnData: string }>> {
  try {
    return (await multicall.callStatic.aggregate3(calls)) as Array<{
      success: boolean
      returnData: string
    }>
  } catch (error) {
    if ((error as { code?: string })?.code !== 'CALL_EXCEPTION') throw error
    if (calls.length === 1) return [{ success: false, returnData: '0x' }]
    const middle = Math.ceil(calls.length / 2)
    const [head, tail] = await Promise.all([
      callChunk(multicall, calls.slice(0, middle)),
      callChunk(multicall, calls.slice(middle))
    ])
    return [...head, ...tail]
  }
}

async function runRound(
  provider: ethers.providers.Provider,
  rows: Row[],
  signal?: AbortSignal
): Promise<number> {
  if (rows.length === 0) return 0
  const multicall = new ethers.Contract(
    MULTICALL3,
    multicallIface as unknown as ethers.ContractInterface,
    provider
  )
  const calls = rows.map((r) => ({
    target: r.call.target,
    allowFailure: r.call.allowFailure,
    callData: r.call.callData
  }))
  const chunks: Array<typeof calls> = []
  for (let i = 0; i < calls.length; i += QUOTE_CHUNK) chunks.push(calls.slice(i, i + QUOTE_CHUNK))
  const batched = await Promise.all(chunks.map((chunk) => callChunk(multicall, chunk)))
  const results: Array<{ success: boolean; returnData: string }> = batched.flat()
  if (signal?.aborted) throw new DOMException('aborted', 'AbortError')
  let failed = 0
  for (let i = 0; i < rows.length; i++) {
    const row = results[i]
    if (!row || !row.success) {
      failed += 1
      continue
    }
    try {
      rows[i].slot(rows[i].call.decode(row.returnData))
    } catch {
      failed += 1
    }
  }
  return failed
}

export interface QuoteResult {
  quotes: Quote[]
  /** How many individual quote calls came back empty. Diagnostics only. */
  failedCalls: number
}

/**
 * Prices every candidate for a pair.
 *
 * Deviation from scope 13 §4.3, recorded here and in the checklist: the
 * reference leg of a two-hop route is quoted from the first hop's own
 * reference output, rather than from the full-size first-hop output divided by
 * a thousand. That division carries the first hop's price impact into the
 * reference, where it cancels against the same impact in the real quote — so
 * the number shown for a two-hop route was the second hop's impact alone, with
 * the first hop's invisible. Understating that warning is the one direction
 * that costs money. The fix is a few extra rows in a round that already
 * carries a hundred and seventy, and it changes neither the number of rounds
 * nor the pruning that §4.3 exists to protect.
 */
export async function quoteRoutes(params: {
  provider: ethers.providers.Provider
  pools: Map<string, PoolRef[]>
  tokenIn: string
  tokenOut: string
  hubs: string[]
  amountIn: bigint
  signal?: AbortSignal
}): Promise<QuoteResult> {
  const { provider, pools, tokenIn, tokenOut, hubs, amountIn, signal } = params
  const inAddr = tokenIn.toLowerCase()
  const outAddr = tokenOut.toLowerCase()
  const referenceIn = amountIn / REFERENCE_DIVISOR
  const wantReference = referenceIn > 0n

  const quotes: Quote[] = []
  let failedCalls = 0

  // ── Round 1 ───────────────────────────────────────────────────────────────
  const rows: Row[] = []

  const direct = directCandidates(pools, inAddr, outAddr)
  const directOut = new Map<number, bigint>()
  const directRefOut = new Map<number, bigint>()
  direct.forEach((candidate, i) => {
    rows.push({ call: quoteCallFor(candidate, amountIn), slot: (a) => directOut.set(i, a) })
    if (wantReference) {
      rows.push({ call: quoteCallFor(candidate, referenceIn), slot: (a) => directRefOut.set(i, a) })
    }
  })

  const groups = hopGroups(pools, inAddr, outAddr, hubs)
  interface FirstHop {
    group: HopGroup
    pool: PoolRef
    out?: bigint
    refOut?: bigint
  }
  const firstHops: FirstHop[] = []
  for (const group of groups) {
    for (const pool of group.first) {
      const hop: FirstHop = { group, pool }
      firstHops.push(hop)
      const candidate = singleHopCandidate(inAddr, group.hub, pool)
      rows.push({ call: quoteCallFor(candidate, amountIn), slot: (a) => { hop.out = a } })
      if (wantReference) {
        rows.push({ call: quoteCallFor(candidate, referenceIn), slot: (a) => { hop.refOut = a } })
      }
    }
  }

  failedCalls += await runRound(provider, rows, signal)

  direct.forEach((candidate, i) => {
    const out = directOut.get(i)
    if (out === undefined || out === 0n) return
    const refOut = directRefOut.get(i)
    quotes.push({
      candidate,
      amountIn,
      amountOut: out,
      amountOutAtReferencePrice: refOut,
      priceImpactBps:
        refOut === undefined ? undefined : priceImpactBps(amountIn, out, referenceIn, refOut)
    })
  })

  // ── Round 2 ───────────────────────────────────────────────────────────────
  /*
   * One first hop per (hub, venue): the one that returned most. Keeping them
   * all would make round two the product of two pool sets, and every extra
   * pairing is a row in a multicall that has to come back before the user sees
   * a number.
   */
  const bestFirst = new Map<string, FirstHop>()
  for (const hop of firstHops) {
    if (hop.out === undefined || hop.out === 0n) continue
    const key = `${hop.group.hub}:${hop.group.venue}`
    const current = bestFirst.get(key)
    if (!current || (current.out ?? 0n) < hop.out) bestFirst.set(key, hop)
  }

  interface SecondHop {
    hop: FirstHop
    pool: PoolRef
    out?: bigint
    refOut?: bigint
  }
  const secondRows: Row[] = []
  const secondHops: SecondHop[] = []
  for (const hop of bestFirst.values()) {
    for (const pool of hop.group.second) {
      const entry: SecondHop = { hop, pool }
      secondHops.push(entry)
      const candidate = singleHopCandidate(hop.group.hub, outAddr, pool)
      secondRows.push({
        call: quoteCallFor(candidate, hop.out as bigint),
        slot: (a) => { entry.out = a }
      })
      if (hop.refOut !== undefined && hop.refOut > 0n) {
        secondRows.push({
          call: quoteCallFor(candidate, hop.refOut),
          slot: (a) => { entry.refOut = a }
        })
      }
    }
  }

  failedCalls += await runRound(provider, secondRows, signal)

  for (const entry of secondHops) {
    if (entry.out === undefined || entry.out === 0n) continue
    const candidate: RouteCandidate = {
      venue: entry.pool.venue,
      path: [inAddr, entry.hop.group.hub, outAddr],
      pools: [entry.hop.pool, entry.pool]
    }
    quotes.push({
      candidate,
      amountIn,
      amountOut: entry.out,
      amountOutAtReferencePrice: entry.refOut,
      priceImpactBps:
        entry.refOut === undefined
          ? undefined
          : priceImpactBps(amountIn, entry.out, referenceIn, entry.refOut)
    })
  }

  return { quotes, failedCalls }
}
